/**
 * Breadcrumbs — auto-derives a breadcrumb trail from the current URL.
 *
 * Hidden on the landing page (`/`), the register flow, the checkout flow,
 * and admin routes (admin has its own layout).
 *
 * Static segments (`catalog`, `learn`, `dashboard`, etc.) get pretty labels
 * from {@link STATIC_LABELS}. Dynamic segments (slugs / IDs) are passed
 * through with `-` and `_` converted to spaces and Title Cased.
 *
 * Pages that need to override the trailing label (e.g. ProductDetail to show
 * the product name instead of its slug) can pass `<Breadcrumbs trailLabel=… />`
 * directly. The auto-derived bar in `AppLayout` is hidden on those routes via
 * the `HIDDEN_PREFIXES` list.
 */
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { ROUTE_LABELS } from '@/lib/routes'

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * Pretty labels for known top-level segments. The map covers every static
 * route in `App.tsx`. Slugs / IDs at deeper levels fall through to the
 * `prettify()` helper.
 *
 * Merges with the centralized ROUTE_LABELS from `@/lib/routes`.
 */
const STATIC_LABELS: Record<string, string> = {
  ...ROUTE_LABELS,
  product: 'Product',
  contract: 'Contract',
  bundle: 'Bundle',
}

/**
 * Routes where the auto-derived breadcrumb bar should NOT render — usually
 * because the page already renders its own breadcrumbs (with richer context
 * like the product name) or because breadcrumbs would be visually noisy.
 *
 * Note: `/checkout/success` is explicitly NOT hidden — it benefits from
 * showing the full trail (Home > Checkout > Order Complete).
 */
const HIDDEN_PREFIXES: string[] = [
  '/register',            // Full-page registration
  '/checkout/',           // Multi-step funnel — own header (except /checkout/success handled below)
  '/catalog/',            // ProductDetail renders its own breadcrumb
  '/marketplace/',        // ProductDetail renders its own breadcrumb
  '/contracts/',          // ContractDetail renders its own breadcrumb
  '/bundles/',            // BundleDetail renders its own breadcrumb
  '/learn',               // Learn has a custom sticky sidebar layout
  '/learn/',              // Article renders its own breadcrumb
]

/** Exact paths to hide (checked before prefix match). */
const HIDDEN_EXACT: string[] = [
  '/checkout',            // Root checkout
]

function shouldHide(pathname: string): boolean {
  // Exact landing match
  if (pathname === '/') return true
  // Exact hidden paths
  if (HIDDEN_EXACT.includes(pathname)) return true
  // Allow /checkout/success through (it's a confirmation page, benefits from breadcrumbs)
  if (pathname === '/checkout/success') return false
  // Prefix match — anything in HIDDEN_PREFIXES that ends with `/` matches
  // descendant routes; without `/` it matches only the exact path.
  return HIDDEN_PREFIXES.some((prefix) =>
    prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function prettify(segment: string): string {
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

interface Crumb {
  label: string
  href: string
  isLast: boolean
}

function buildCrumbs(pathname: string): Crumb[] {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return []

  const crumbs: Crumb[] = []
  let acc = ''
  segments.forEach((seg, i) => {
    acc += `/${seg}`
    const label = STATIC_LABELS[seg] ?? prettify(decodeURIComponent(seg))
    crumbs.push({
      label,
      href: acc,
      isLast: i === segments.length - 1,
    })
  })
  return crumbs
}

// ─── Public component ────────────────────────────────────────────────────────

interface BreadcrumbsProps {
  /** Override the trailing crumb label (e.g. for "Acme Token" instead of "acme-token"). */
  trailLabel?: string
  /** Render even on routes that are normally hidden. */
  force?: boolean
  /** Extra wrapper classes. */
  className?: string
}

export function Breadcrumbs({ trailLabel, force, className }: BreadcrumbsProps) {
  const { pathname } = useLocation()

  if (!force && shouldHide(pathname)) return null

  const crumbs = buildCrumbs(pathname)
  if (crumbs.length === 0) return null

  // Apply trail-label override
  if (trailLabel && crumbs.length > 0) {
    crumbs[crumbs.length - 1] = {
      ...crumbs[crumbs.length - 1],
      label: trailLabel,
    }
  }

  return (
    <nav
      aria-label="Breadcrumb"
      className={[
        'border-b border-[#141414] bg-[#0A0A0A]',
        className ?? '',
      ].join(' ')}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
        <ol className="flex items-center gap-1 text-xs sm:text-sm flex-wrap">
          {/* Home */}
          <li>
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-[#6B6B6B] hover:text-[#E8850C] transition-colors duration-200"
              aria-label="Home"
            >
              <Home size={13} />
              <span className="hidden sm:inline">Home</span>
            </Link>
          </li>

          {/* Crumb trail */}
          {crumbs.map((crumb) => (
            <li key={crumb.href} className="flex items-center gap-1 min-w-0">
              <ChevronRight size={12} className="shrink-0 text-[#2A2A2A]" />
              {crumb.isLast ? (
                <span
                  aria-current="page"
                  className="text-white font-medium truncate max-w-[200px] sm:max-w-[300px]"
                  title={crumb.label}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.href}
                  className="text-[#6B6B6B] hover:text-[#E8850C] transition-colors duration-200 truncate max-w-[160px] sm:max-w-[220px]"
                  title={crumb.label}
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  )
}
